import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaBullseye, 
  FaEye, 
  FaUsers, 
  FaAward,
  FaGlobeAfrica,
  FaLightbulb,
  FaRocket,
  FaHandshake,
  FaChartLine,
  FaHeart,
  FaCheckCircle
} from 'react-icons/fa';

const LXDAbout = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const team = [
    {
      name: "Dr. Abebe Kebede",
      role: "Founder & CEO",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300",
      bio: "Former professor at Addis Ababa University with 20+ years in education technology."
    },
    {
      name: "Eng. Selamawit Bekele",
      role: "CTO",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300",
      bio: "Software engineer with experience at Google and Microsoft, passionate about EdTech."
    },
    {
      name: "Prof. Dawit Haile",
      role: "Head of Curriculum",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300",
      bio: "Education expert specializing in curriculum development for African contexts."
    },
    {
      name: "Mrs. Tigist Haile",
      role: "Head of Partnerships",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300",
      bio: "Business development expert with strong connections across Ethiopian industries."
    }
  ];

  const milestones = [
    { year: "2020", title: "Founded", description: "LXD Ethiopia was established with a vision to digitize Ethiopian education." },
    { year: "2021", title: "First 1000 Students", description: "Reached our first major milestone with 1000 active learners." },
    { year: "2022", title: "Partnership Launch", description: "Formed partnerships with major Ethiopian universities and companies." },
    { year: "2023", title: "50,000 Learners", description: "Expanded to serve 50,000+ students across Ethiopia." },
    { year: "2024", title: "Pan-African Expansion", description: "Began expansion to other African countries." }
  ];

  const values = [
    {
      icon: <FaLightbulb className="text-4xl" />,
      title: "Innovation",
      description: "We constantly push boundaries to create cutting-edge learning experiences tailored for Ethiopian learners."
    },
    {
      icon: <FaHeart className="text-4xl" />,
      title: "Accessibility",
      description: "Education should be accessible to all. We ensure our platform works on any device, anywhere in Ethiopia."
    },
    {
      icon: <FaGlobeAfrica className="text-4xl" />,
      title: "Local Context",
      description: "Our courses are designed with Ethiopian culture, language, and market needs in mind."
    },
    {
      icon: <FaRocket className="text-4xl" />,
      title: "Excellence",
      description: "We maintain the highest standards in course quality, instruction, and student support."
    }
  ];

  const stats = [
    { number: "50,000+", label: "Students", icon: <FaUsers /> },
    { number: "200+", label: "Courses", icon: <FaGraduationCap /> },
    { number: "50+", label: "Partners", icon: <FaHandshake /> },
    { number: "95%", label: "Satisfaction", icon: <FaAward /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 via-blue-900 to-cyan-900 text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-lg"
            >
              <FaGlobeAfrica className="text-4xl" />
            </motion.div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              Transforming Ethiopian Education
            </h1>
            
            <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl">
              LXD Ethiopia is on a mission to democratize access to quality education across Ethiopia through innovative digital learning solutions.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link
                to="/courses"
                className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2"
              >
                Explore Courses <FaGraduationCap />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2"
              >
                Get in Touch <FaHandshake />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 lg:p-12">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <FaBullseye className="text-3xl text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To bridge the educational gap in Ethiopia by providing accessible, affordable, and high-quality digital learning experiences that empower individuals to transform their lives and contribute to Ethiopia's development.
              </p>
              <ul className="mt-6 space-y-3">
                {["Democratize access to education", "Bridge skill gaps in Ethiopian workforce", "Foster innovation and entrepreneurship", "Preserve and promote Ethiopian knowledge"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <FaCheckCircle className="text-purple-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyan-50 to-emerald-50 rounded-3xl p-8 lg:p-12">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                <FaEye className="text-3xl text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To become Africa's leading digital learning platform, setting the standard for quality, accessibility, and innovation in education while preserving and promoting African knowledge and culture.
              </p>
              <ul className="mt-6 space-y-3">
                {["Lead African EdTech innovation", "Serve millions across the continent", "Create global impact from Ethiopia", "Build sustainable education ecosystem"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <FaCheckCircle className="text-cyan-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold mb-2">{stat.number}</div>
                <div className="text-xl text-white/80 flex items-center justify-center gap-2">
                  {stat.icon} {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Our Story</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From a small idea to a movement transforming Ethiopian education
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-purple-600 to-blue-600" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}
                >
                  <div className="flex-1">
                    <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all">
                      <div className="text-4xl font-bold text-purple-600 mb-2">{milestone.year}</div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="hidden lg:flex w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full items-center justify-center text-white font-bold text-xl z-10">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 text-center hover:shadow-xl transition-all"
              >
                <div className="text-purple-600 mb-4 flex justify-center">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Passionate experts dedicated to transforming Ethiopian education
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="relative">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-600/80 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-purple-600 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Join Our Mission</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Be part of the movement transforming Ethiopian education. Whether you're a student, instructor, or partner, there's a place for you at LXD Ethiopia.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/courses"
                className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
              >
                Start Learning
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
              >
                Partner With Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LXDAbout;
